import { FC, useEffect, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Modal, Upload, Button, Image, Typography, Tooltip, message as antMessage } from "antd";
import { InboxOutlined, FilePdfOutlined, FileExcelOutlined, FileOutlined, DeleteOutlined } from "@ant-design/icons";
import type { RcFile } from "antd/es/upload";
import styled from "@emotion/styled";
import { uploadFile, updatePayoutFileList } from "services/factoryAPI";
import { renameUploadFile } from "helpers/utils";
import { useGetBankById } from "hooks/useFactory";
import { isCorporateBankType } from "components/ManageBank/CreateBank/bankDocumentConfig";
import ReceiptTemplateNotice, { type ReceiptNoticeVariant } from "./ReceiptTemplateNotice";

interface ModalUploadReceiptProps {
  open: boolean;
  payoutId: string | null;
  /** ไฟล์ใบเสร็จเดิม (file_list) ที่อัพโหลดไว้แล้ว — เปิดมาให้เพิ่ม/ลบต่อได้ */
  existingUrls?: string[];
  /** บัญชีรับเงินของรายการ — ใช้เลือกคำแนะนำ + แบบฟอร์มใบเสร็จ (นิติบุคคล / บุคคลธรรมดา) */
  bankFactoryId?: string | null;
  onClose: () => void;
  onSuccess: (id: string, fileList: string[]) => void;
}

interface PickedFile {
  uid: string;
  name: string;
  file: File;
  previewUrl: string;
  kind: FileKind;
}

type FileKind = "image" | "pdf" | "excel";

const ACCEPT_TYPES = "image/*,.pdf,.xlsx";
const XLSX_MIME = "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";

// ดูชนิดไฟล์จากนามสกุล (ตัด query string ออกก่อน) — ใช้ได้ทั้งชื่อไฟล์และ url
const fileKindFromName = (name: string, mime = ""): FileKind => {
  const lower = name.toLowerCase().split("?")[0];
  if (lower.endsWith(".pdf") || mime === "application/pdf") return "pdf";
  if (lower.endsWith(".xlsx") || mime === XLSX_MIME) return "excel";
  return "image";
};

// ไอคอนแทน thumbnail สำหรับไฟล์ที่ไม่ใช่รูป — คลิกแล้วเปิด/ดาวน์โหลดไฟล์
const DocThumb: FC<{ url: string; kind: FileKind }> = ({ url, kind }) => {
  if (kind === "pdf") {
    return (
      <a href={url} target="_blank" rel="noreferrer" className="pdf-thumb">
        <FilePdfOutlined />
        <span>PDF</span>
      </a>
    );
  }
  if (kind === "excel") {
    return (
      <a href={url} target="_blank" rel="noreferrer" className="excel-thumb">
        <FileExcelOutlined />
        <span>Excel</span>
      </a>
    );
  }
  return (
    <a href={url} target="_blank" rel="noreferrer" className="file-thumb">
      <FileOutlined />
      <span>เปิดไฟล์</span>
    </a>
  );
};

const fileNameFromUrl = (url: string) => {
  try {
    return decodeURIComponent(url.split("/").pop()?.split("?")[0] || "ไฟล์");
  } catch {
    return "ไฟล์";
  }
};

// thumbnail ของไฟล์เดิม (จาก url) — ลองแสดงรูปก่อน ถ้าไม่ใช่รูป/โหลดไม่ได้ค่อย fallback เป็นไอคอนไฟล์
const ExistingThumb: FC<{ url: string }> = ({ url }) => {
  const [imgError, setImgError] = useState(false);
  const kind = fileKindFromName(url);
  if (kind !== "image" || imgError) {
    return <DocThumb url={url} kind={kind} />;
  }
  return (
    <Image src={url} width="100%" height="100%" style={{ objectFit: "cover" }} onError={() => setImgError(true)} />
  );
};

const ModalUploadReceipt: FC<ModalUploadReceiptProps> = ({
  open,
  payoutId,
  existingUrls = [],
  bankFactoryId,
  onClose,
  onSuccess,
}) => {
  const queryClient = useQueryClient();
  // บัญชีนิติบุคคล → คำแนะนำนิติบุคคล (จด VAT / ไม่จด VAT) · นอกนั้นทั้งหมด (รวมไม่มี bank id / ไม่มี type /
  // API error ที่ตอบ 200 แต่ data ว่าง) → บุคคลธรรมดา · ระหว่างโหลดยังไม่แสดง กันกล่องสลับเนื้อหาให้เห็น
  const { data: bankData, isLoading: isBankLoading } = useGetBankById(bankFactoryId ?? "", {
    enabled: open && !!bankFactoryId,
  });
  const showTemplate = !isBankLoading;
  const noticeVariant: ReceiptNoticeVariant = isCorporateBankType(bankData?.data?.[0]?.bank_type_name)
    ? "corporate"
    : "individual";
  const [keptUrls, setKeptUrls] = useState<string[]>([]);
  const [files, setFiles] = useState<PickedFile[]>([]);
  const [uploading, setUploading] = useState(false);

  // seed ไฟล์เดิมเมื่อเปิด modal
  useEffect(() => {
    if (open) setKeptUrls(existingUrls);
  }, [open, existingUrls]);

  const revokeAll = (items: PickedFile[]) => {
    items.forEach((item) => URL.revokeObjectURL(item.previewUrl));
  };

  const handleClose = () => {
    revokeAll(files);
    setFiles([]);
    setKeptUrls([]);
    onClose();
  };

  const handleBeforeUpload = (file: RcFile) => {
    //change file name
    const kind = fileKindFromName(file.name, file.type);
    // กันไฟล์ชนิดอื่นที่ลากมาวาง (accept กันได้แค่ตอนเลือกจาก dialog)
    if (kind === "image" && !file.type.startsWith("image/")) {
      antMessage.error(`ไม่รองรับไฟล์ ${file.name} — ใช้ได้เฉพาะรูปภาพ, PDF และ Excel (.xlsx)`);
      return Upload.LIST_IGNORE;
    }
    const newFile = renameUploadFile(file);
    setFiles((prev) => [
      ...prev,
      { uid: file.uid, name: newFile.name, file: newFile, previewUrl: URL.createObjectURL(newFile), kind },
    ]);
    return false;
  };

  const handleRemoveNew = (uid: string) => {
    setFiles((prev) => {
      const target = prev.find((item) => item.uid === uid);
      if (target) URL.revokeObjectURL(target.previewUrl);
      return prev.filter((item) => item.uid !== uid);
    });
  };

  const handleRemoveExisting = (url: string) => {
    setKeptUrls((prev) => prev.filter((u) => u !== url));
  };

  const hasChange = files.length > 0 || keptUrls.length !== existingUrls.length;
  const totalCount = keptUrls.length + files.length;

  const handleConfirm = async () => {
    if (!payoutId || !hasChange) return;
    setUploading(true);
    try {
      const uploaded = await Promise.all(files.map((item) => uploadFile(item.file)));
      const finalUrls = [...keptUrls, ...uploaded.map((res) => res.url)];
      await updatePayoutFileList(payoutId, finalUrls);
      await queryClient.invalidateQueries({ queryKey: ["payoutBatchSummary"] });
      antMessage.success("บันทึกใบเสร็จสำเร็จ");
      onSuccess(payoutId, finalUrls);
      handleClose();
    } catch {
      antMessage.error("บันทึกไม่สำเร็จ กรุณาลองใหม่");
    } finally {
      setUploading(false);
    }
  };

  return (
    <Modal
      title="จัดการใบเสร็จรับเงิน"
      open={open}
      onCancel={handleClose}
      maskClosable={false}
      footer={[
        <Button key="cancel" onClick={handleClose} disabled={uploading}>
          ยกเลิก
        </Button>,
        <Button
          key="confirm"
          type="primary"
          loading={uploading}
          disabled={!hasChange}
          onClick={handleConfirm}
        >
          บันทึก{totalCount > 0 ? ` (${totalCount})` : ""}
        </Button>,
      ]}
      width={showTemplate ? 960 : 560}
      style={{ top: 24 }}
    >
      {/* มีคำแนะนำ → 2 คอลัมน์ (คำแนะนำซ้าย / อัปโหลดขวา) จอแคบค่อยเรียงลงมา */}
      <Layout withTemplate={showTemplate}>
        {showTemplate && <ReceiptTemplateNotice variant={noticeVariant} />}

        <UploadColumn>
          <Upload.Dragger
            accept={ACCEPT_TYPES}
            multiple
            beforeUpload={handleBeforeUpload}
            showUploadList={false}
            disabled={uploading}
          >
            <p className="ant-upload-drag-icon">
              <InboxOutlined />
            </p>
            <p className="ant-upload-text">คลิกหรือลากไฟล์มาวางที่นี่</p>
            <p className="ant-upload-hint">รองรับไฟล์รูปภาพ, PDF และ Excel (.xlsx) (เลือกได้หลายไฟล์)</p>
          </Upload.Dragger>

          <Image.PreviewGroup>
            {keptUrls.length > 0 && (
              <>
                <Typography.Text type="secondary" style={{ display: "block", margin: "16px 0 8px" }}>
                  ไฟล์ที่อัพโหลดแล้ว ({keptUrls.length})
                </Typography.Text>
                <PreviewGrid>
                  {keptUrls.map((url) => (
                    <PreviewCard key={url}>
                      <ThumbWrapper>
                        <ExistingThumb url={url} />
                        {!uploading && (
                          <Tooltip title="ลบไฟล์">
                            <RemoveButton
                              type="text"
                              size="small"
                              icon={<DeleteOutlined />}
                              onClick={() => handleRemoveExisting(url)}
                            />
                          </Tooltip>
                        )}
                      </ThumbWrapper>
                      <Tooltip title={fileNameFromUrl(url)}>
                        <FileName>{fileNameFromUrl(url)}</FileName>
                      </Tooltip>
                    </PreviewCard>
                  ))}
                </PreviewGrid>
              </>
            )}

            {files.length > 0 && (
              <>
                <Typography.Text type="secondary" style={{ display: "block", margin: "16px 0 8px" }}>
                  ไฟล์ที่จะเพิ่ม ({files.length})
                </Typography.Text>
                <PreviewGrid>
                  {files.map((item) => (
                    <PreviewCard key={item.uid}>
                      <ThumbWrapper>
                        {item.kind !== "image" ? (
                          <DocThumb url={item.previewUrl} kind={item.kind} />
                        ) : (
                          <Image src={item.previewUrl} width="100%" height="100%" style={{ objectFit: "cover" }} />
                        )}
                        {!uploading && (
                          <Tooltip title="ลบไฟล์">
                            <RemoveButton
                              type="text"
                              size="small"
                              icon={<DeleteOutlined />}
                              onClick={() => handleRemoveNew(item.uid)}
                            />
                          </Tooltip>
                        )}
                      </ThumbWrapper>
                      <Tooltip title={item.name}>
                        <FileName>{item.name}</FileName>
                      </Tooltip>
                    </PreviewCard>
                  ))}
                </PreviewGrid>
              </>
            )}
          </Image.PreviewGroup>
        </UploadColumn>
      </Layout>
    </Modal>
  );
};

export default ModalUploadReceipt;

const Layout = styled.div<{ withTemplate: boolean }>`
  display: grid;
  grid-template-columns: ${({ withTemplate }) => (withTemplate ? "minmax(0, 1fr) 320px" : "minmax(0, 1fr)")};
  gap: 20px;
  align-items: stretch;

  @media (max-width: 768px) {
    grid-template-columns: minmax(0, 1fr);
    gap: 16px;
  }
`;

/* กล่องลากวางยืดเต็มความสูงคอลัมน์ (เท่ากล่องคำแนะนำ) — มีไฟล์แล้วค่อยหดให้รายการไฟล์ */
const UploadColumn = styled.div`
  display: flex;
  flex-direction: column;
  min-width: 0;

  & > .ant-upload-wrapper {
    flex: 1;
    display: flex;
    flex-direction: column;
    min-height: 180px;
  }

  & > .ant-upload-wrapper > .ant-upload-drag {
    flex: 1;
  }

  & > .ant-upload-wrapper .ant-upload-btn {
    display: flex;
    flex-direction: column;
    justify-content: center;
    height: 100%;
  }
`;

const PreviewGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(120px, 1fr));
  gap: 12px;
`;

const PreviewCard = styled.div`
  border: 1px solid #f0f0f0;
  border-radius: 8px;
  overflow: hidden;
  background: #fafafa;
`;

const ThumbWrapper = styled.div`
  position: relative;
  width: 100%;
  aspect-ratio: 1 / 1;
  background: #fff;

  .ant-image {
    width: 100%;
    height: 100%;
  }

  .pdf-thumb,
  .excel-thumb,
  .file-thumb {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 4px;
    width: 100%;
    height: 100%;
    font-size: 32px;

    span {
      font-size: 13px;
      font-weight: 600;
    }
  }

  .pdf-thumb {
    color: #cf1322;
  }

  .excel-thumb {
    color: #217346;
  }

  .file-thumb {
    color: #1677ff;
  }
`;

const RemoveButton = styled(Button)`
  position: absolute;
  top: 4px;
  right: 4px;
  background: rgba(255, 255, 255, 0.9);
  color: #cf1322;

  &:hover {
    background: #fff !important;
    color: #ff4d4f !important;
  }
`;

const FileName = styled.div`
  padding: 6px 8px;
  font-size: 12px;
  color: #595959;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;
